import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useDebouncedValue } from "@mantine/hooks";
import { ToggleItem } from "@onengine/component-library";
import { MultiSelect, type SelectOption } from "@/components/ui/Select/MultiSelect";
import { useEntityLabelDropdown } from "@/hooks/entities/useEntitiesQueries";
import { useActiveSession } from "@/hooks/useActiveSession";
import { PLATFORM_TENANT_ID, useEntityTenantStore } from "@/store/entityTenantStore";
import {
  buildBrandOptions,
  buildClientOptions,
  deriveScopeSummary,
  getSelectedClientIds,
  getVisiblePickers,
  pruneSelection,
  rememberItems,
  withSelectedItems,
  type NamedItem,
} from "./scopeTargets";
import { useScopeBrands, useScopeClients } from "./useScopeLookups";
import styles from "./ScopeTargetsPicker.module.scss";

interface ScopeTargetsPickerProps {
  opened: boolean;
  levelOptions: SelectOption[];
  levelLoading: boolean;
  levelError?: string;
  canPickEntities: boolean;
  /** The entity a form opened from an assignment slot belongs to, whoever is signed in. */
  lockedEntity?: { id: string; name?: string };
}

const SEARCH_DEBOUNCE_MS = 300;

const toLabelMap = (items: NamedItem[]) =>
  Object.fromEntries(items.map((item) => [item.id, item.label]));

const sameKeys = (a: string[], b: string[]) =>
  a.length === b.length && a.every((value, index) => value === b[index]);

export function ScopeTargetsPicker({
  opened,
  levelOptions,
  levelLoading,
  levelError,
  canPickEntities,
  lockedEntity,
}: ScopeTargetsPickerProps) {
  const { control, setValue } = useFormContext();
  const entityTenant = useEntityTenantStore((state) => state.entityTenant);
  const { tenantId: sessionTenantId, entityName } = useActiveSession();

  const levelIds = useWatch({ control, name: "levelIds" }) as string[];
  const entityIds = useWatch({ control, name: "entityIds" }) as string[];
  const clientKeys = useWatch({ control, name: "clientKeys" }) as string[];
  const brandKeys = useWatch({ control, name: "brandKeys" }) as string[];

  const pickers = useMemo(() => {
    const selectedLevels = new Set(levelIds);
    return getVisiblePickers(
      levelOptions.filter((option) => selectedLevels.has(option.value)).map((o) => o.label),
    );
  }, [levelIds, levelOptions]);
  const currentEntityId = lockedEntity?.id ?? (entityTenant || sessionTenantId || "");
  const currentEntityName = lockedEntity ? lockedEntity.name : entityName;

  const [entitySearch, setEntitySearch] = useState("");
  const [clientSearch, setClientSearch] = useState("");
  const [brandSearch, setBrandSearch] = useState("");
  const [debouncedEntitySearch] = useDebouncedValue(entitySearch, SEARCH_DEBOUNCE_MS);
  const [debouncedClientSearch] = useDebouncedValue(clientSearch, SEARCH_DEBOUNCE_MS);
  const [debouncedBrandSearch] = useDebouncedValue(brandSearch, SEARCH_DEBOUNCE_MS);

  const entityCache = useRef<Record<string, string>>({});
  const clientCache = useRef<Record<string, NamedItem>>({});
  const brandCache = useRef<Record<string, NamedItem>>({});

  const { data: entityDropdown, isFetching: isFetchingEntities } = useEntityLabelDropdown(
    opened && canPickEntities && pickers.entities,
    debouncedEntitySearch,
  );

  const entityOptions = useMemo<SelectOption[]>(() => {
    if (canPickEntities) {
      const fetched = (entityDropdown?.data ?? [])
        .filter((option) => option.value !== PLATFORM_TENANT_ID)
        .map((option) => ({ value: option.value, label: option.label }));
      fetched.forEach((option) => {
        entityCache.current[option.value] = option.label;
      });
      const fetchedIds = new Set(fetched.map((option) => option.value));
      const missingSelected = entityIds
        .filter((id) => !fetchedIds.has(id))
        .map((id) => ({ value: id, label: entityCache.current[id] ?? id }));
      return [...fetched, ...missingSelected];
    }
    return currentEntityId
      ? [{ value: currentEntityId, label: currentEntityName || "Your entity" }]
      : [];
  }, [canPickEntities, entityDropdown, entityIds, currentEntityId, currentEntityName]);

  useEffect(() => {
    if (canPickEntities || !pickers.entities || !currentEntityId) return;
    if (!sameKeys(entityIds, [currentEntityId])) setValue("entityIds", [currentEntityId]);
  }, [canPickEntities, pickers.entities, currentEntityId, entityIds, setValue]);

  useEffect(() => {
    const pruned = pruneSelection({ entityIds, clientKeys, brandKeys }, pickers);
    if (!sameKeys(pruned.clientKeys, clientKeys)) setValue("clientKeys", pruned.clientKeys);
    if (!sameKeys(pruned.brandKeys, brandKeys)) setValue("brandKeys", pruned.brandKeys);
  }, [entityIds, clientKeys, brandKeys, pickers, setValue]);

  const selection = pruneSelection({ entityIds, clientKeys, brandKeys }, pickers);
  const entityLabels = toLabelMap(
    entityOptions.map((option) => ({ id: option.value, label: option.label })),
  );
  const selectedEntities = entityIds.map((id) => ({ id, label: entityLabels[id] ?? id }));

  const clientLookup = useScopeClients(entityIds, pickers.clients, debouncedClientSearch);
  rememberItems(clientCache.current, clientLookup.itemsByParent);
  const clientItemsByParent = withSelectedItems(
    clientLookup.itemsByParent,
    clientKeys,
    clientCache.current,
  );
  const clientLabels = toLabelMap(Object.values(clientItemsByParent).flat());
  const selectedClientIds = getSelectedClientIds(selection);
  const selectedClients = selectedClientIds.map((id) => ({ id, label: clientLabels[id] ?? id }));

  const brandLookup = useScopeBrands(selectedClientIds, pickers.brands, debouncedBrandSearch);
  rememberItems(brandCache.current, brandLookup.itemsByParent);
  const brandItemsByParent = withSelectedItems(
    brandLookup.itemsByParent,
    brandKeys,
    brandCache.current,
  );
  const brandLabels = toLabelMap(Object.values(brandItemsByParent).flat());

  const clientOptions = buildClientOptions(selectedEntities, clientItemsByParent, clientKeys);
  const brandOptions = buildBrandOptions(selectedClients, brandItemsByParent, brandKeys);

  const summaryRows = deriveScopeSummary(selection, pickers, {
    entities: entityLabels,
    clients: clientLabels,
    brands: brandLabels,
  });
  const targetsSignature = JSON.stringify(summaryRows.map((row) => row.target));
  useEffect(() => {
    setValue("scopeTargets", JSON.parse(targetsSignature));
  }, [targetsSignature, setValue]);

  const hasEntity = entityIds.length > 0;
  const hasSpecificClient = selectedClientIds.length > 0;
  const brandsHint = hasEntity
    ? "Pick a client above to choose its brands"
    : "Pick an entity above to choose its brands";

  return (
    <section className={styles.section} aria-label="Applies to">
      <div className={styles.header}>
        <span className={styles.title}>Applies to</span>
        <span className={styles.optional}>Optional</span>
      </div>

      <MultiSelect
        name="levelIds"
        label="Level"
        placeholder="Select all that apply…"
        options={levelOptions}
        loading={levelLoading}
        error={levelError}
        size="small"
        wrapperClassName={styles.field}
        showCheckboxes
      />

      {pickers.entities && (
        <MultiSelect
          name="entityIds"
          label="Entities"
          placeholder="Select all that apply…"
          options={entityOptions}
          searchValue={entitySearch}
          onSearchChange={setEntitySearch}
          loading={isFetchingEntities}
          readOnly={!canPickEntities}
          hint={canPickEntities ? undefined : "Forms are scoped to your current entity"}
          size="small"
          wrapperClassName={styles.field}
          showCheckboxes
        />
      )}

      {pickers.clients && (
        <MultiSelect
          name="clientKeys"
          label="Clients"
          placeholder="Select all that apply…"
          options={clientOptions}
          searchValue={clientSearch}
          onSearchChange={setClientSearch}
          loading={clientLookup.isLoading}
          disabled={!hasEntity}
          hint={hasEntity ? undefined : "Pick an entity above to choose its clients"}
          size="small"
          wrapperClassName={styles.field}
          showCheckboxes
        />
      )}

      {pickers.brands && (
        <MultiSelect
          name="brandKeys"
          label="Brands"
          placeholder="Select all that apply…"
          options={brandOptions}
          searchValue={brandSearch}
          onSearchChange={setBrandSearch}
          loading={brandLookup.isLoading}
          disabled={!hasEntity || !hasSpecificClient}
          hint={hasEntity && hasSpecificClient ? undefined : brandsHint}
          size="small"
          wrapperClassName={styles.field}
          showCheckboxes
        />
      )}

      {summaryRows.length > 0 && (
        <>
          <div>
            <Controller
              name="createAssignments"
              control={control}
              render={({ field }) => (
                <ToggleItem
                  checked={field.value}
                  onChange={field.onChange}
                  size="sm"
                  intent="brand"
                  primary="Create these assignments when I publish"
                />
              )}
            />
            <span className={styles.helper}>
              Uses this form&apos;s Work Order Type, Visit Type and trades.
            </span>
          </div>

          <div className={styles.summary}>
            <span className={styles.summaryTitle}>
              Assignments on publish · {summaryRows.length}
            </span>
            <ul className={styles.summaryList}>
              {summaryRows.map((row) => (
                <li key={row.key}>{row.label}</li>
              ))}
            </ul>
          </div>
        </>
      )}
    </section>
  );
}
